import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { uid } from '../../lib/storage';
import { useLiveTickers, useTickerStore } from '../../lib/tickers';
import { tickerOf } from '../../lib/symbols';
import type { SymbolRef } from '../../lib/symbols';
import { formatPrice } from '../../lib/format';
import { toast } from '../../components/ui/Toast';

export type AlertCondition = 'crossing' | 'above' | 'below';

export interface PriceAlert {
  id: string;
  exchangeId: string;
  pair: string;
  condition: AlertCondition;
  price: number;
  note: string;
  createdAt: number;
  active: boolean;
  triggeredAt?: number;
  /** Last price seen while armed (crossing detection). */
  lastPrice?: number;
}

export interface AlertLogEntry {
  id: string;
  alertId: string;
  at: number;
  price: number;
  text: string;
}

interface AlertsState {
  alerts: PriceAlert[];
  log: AlertLogEntry[];
  add: (alert: Omit<PriceAlert, 'id' | 'createdAt' | 'active'>) => PriceAlert;
  update: (id: string, patch: Partial<PriceAlert>) => void;
  remove: (id: string) => void;
  clearLog: () => void;
}

export const useAlerts = create<AlertsState>()(
  persist(
    (set, get) => ({
      alerts: [],
      log: [],
      add: (alert) => {
        const created: PriceAlert = { ...alert, id: uid('a'), createdAt: Date.now(), active: true };
        set({ alerts: [created, ...get().alerts] });
        return created;
      },
      update: (id, patch) => set({ alerts: get().alerts.map((a) => (a.id === id ? { ...a, ...patch } : a)) }),
      remove: (id) => set({ alerts: get().alerts.filter((a) => a.id !== id) }),
      clearLog: () => set({ log: [] }),
    }),
    { name: 'ch:alerts' },
  ),
);

export function describeAlert(alert: Pick<PriceAlert, 'pair' | 'condition' | 'price'>): string {
  const verb = alert.condition === 'above' ? 'rises above' : alert.condition === 'below' ? 'falls below' : 'crosses';
  return `${tickerOf(alert.pair)} ${verb} ${formatPrice(alert.price)}`;
}

function beep(): void {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    osc.onended = () => void ctx.close();
  } catch {
    // Audio blocked until the page has had a user gesture; the toast still shows.
  }
}

/** Ask once for browser notifications (only after the user creates an alert). */
export async function ensureNotificationPermission(): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  try {
    await Notification.requestPermission();
  } catch {
    // Some browsers only allow the prompt from a click handler; alerts still toast.
  }
}

function fire(alert: PriceAlert, price: number): void {
  const text = `${describeAlert(alert)} — now ${formatPrice(price)}${alert.note ? `. ${alert.note}` : ''}`;
  useAlerts.setState((s) => ({
    alerts: s.alerts.map((a) => (a.id === alert.id ? { ...a, active: false, triggeredAt: Date.now(), lastPrice: price } : a)),
    log: [{ id: uid('g'), alertId: alert.id, at: Date.now(), price, text }, ...s.log].slice(0, 100),
  }));
  toast('Alert triggered', { body: text, tone: 'warn', duration: 0 });
  beep();
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      new Notification('Cryptohopper Charts alert', { body: text, tag: alert.id });
    } catch {
      // Notification constructor unavailable (e.g. some mobile browsers).
    }
  }
}

/** Watches live prices for every armed alert while the site is open. Mount once. */
export function useAlertEngine(): void {
  const alerts = useAlerts((s) => s.alerts);
  const armed = alerts.filter((a) => a.active);
  const refs: SymbolRef[] = armed.map((a) => ({ exchangeId: a.exchangeId, pair: a.pair }));
  useLiveTickers(refs);

  useEffect(() => {
    return useTickerStore.subscribe((state) => {
      const current = useAlerts.getState().alerts;
      for (const alert of current) {
        if (!alert.active) continue;
        const ticker = state.byKey[`${alert.exchangeId}:${alert.pair}`];
        if (!ticker || !Number.isFinite(ticker.last)) continue;
        const price = ticker.last;
        const prev = alert.lastPrice;
        const hit =
          alert.condition === 'above'
            ? price >= alert.price
            : alert.condition === 'below'
              ? price <= alert.price
              : prev !== undefined && (prev - alert.price) * (price - alert.price) <= 0 && prev !== price;
        if (hit) fire(alert, price);
        else if (prev !== price) useAlerts.getState().update(alert.id, { lastPrice: price });
      }
    });
  }, []);
}
